import React, { useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { classNames } from 'primereact/utils';
import { Ripple } from "primereact/ripple";
import { Badge } from 'primereact/badge';
import { MenuItem } from "primereact/menuitem";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface AppSubmenuProps {
    className?: string;
    items?: MenuItem[];
    root?: boolean;
    role?: string;
    onMenuItemClick?: (event: any) => void;
}

const AppSubmenu = (props: AppSubmenuProps) => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const nodeRef = useRef(null);
    const location = useLocation();

    const onMenuItemClick = (
        event: React.MouseEvent<HTMLAnchorElement>,
        item: MenuItem,
        index: any
    ) => {
        // Avoid processing disabled items
        if (item.disabled) {
            event.preventDefault();
            return true;
        }

        // Execute command
        if (item.command) {
            item.command({ originalEvent: event, item: item });
        }

        if (index === activeIndex) setActiveIndex(null);
        else setActiveIndex(index);

        if (props.onMenuItemClick) {
            props.onMenuItemClick({
                originalEvent: event,
                item: item,
            });
        }
    };

    const renderLinkContent = (item: any) => {
        let submenuIcon =
            item.items && (
                <i className="pi pi-fw pi-angle-down menuitem-toggle-icon"></i>
            );
        let badge = item.badge && <Badge value={item.badge} />;

        if (item.fa) {
            let menuIcon = item.icon && item.icon[Object.keys(item.icon)[0]];

            return (
                <React.Fragment>
                    <FontAwesomeIcon
                        icon={menuIcon}
                        size="lg"
                        fixedWidth
                    />
                    <span>{item.label}</span>
                    {submenuIcon}
                    {badge}
                    <Ripple />
                </React.Fragment>
            );
        } else {
            return (
                <React.Fragment>
                    <i className={item.icon}></i>
                    <span>{item.label}</span>
                    {submenuIcon}
                    {badge}
                    <Ripple />
                </React.Fragment>
            );
        }
    };

    const renderLink = (item: any, i: number) => {
        let content = renderLinkContent(item);
        let match = location.pathname.includes(item.to);

        if (item.to) {
            return (
                <NavLink
                    aria-label={item.label}
                    role="menuitem"
                    className={
                        match
                            ? "p-ripple router-link-active router-link-exact-active"
                            : "p-ripple"
                    }
                    to={item.to}
                    onClick={(e) => onMenuItemClick(e, item, i)}
                    target={item.target}
                >
                    {content}
                </NavLink>
            );
        } else {
            return (
                <a
                    aria-label={item.label}
                    role="menuitem"
                    href={item.url}
                    className="p-ripple"
                    onClick={(e) => onMenuItemClick(e, item, i)}
                    target={item.target}
                >
                    {content}
                </a>
            );
        }
    };

    let items =
        props.items &&
        props.items.map((item: any, i: number) => {
            let active = activeIndex === i;
            let styleClass = classNames(item.badgeStyleClass, {
                'layout-menuitem-category': props.root,
                'active-menuitem': active && !item.to,
            });

            if (props.root) {
                return (
                    <li className={styleClass} key={i} role="none">
                        {props.root === true && (
                            <React.Fragment>
                                <div
                                    className="layout-menuitem-root-text"
                                    aria-label={item.label}
                                >
                                    {item.label}
                                </div>
                                <AppSubmenu
                                    items={item.items}
                                    onMenuItemClick={props.onMenuItemClick}
                                />
                            </React.Fragment>
                        )}
                    </li>
                );
            } else {
                return (
                    <li className={styleClass} key={i} role="none">
                        {renderLink(item, i)}
                        <CSSTransition
                            nodeRef={nodeRef}
                            classNames="layout-submenu-wrapper"
                            timeout={{ enter: 1000, exit: 450 }}
                            in={active}
                            unmountOnExit
                        >
                            <div ref={nodeRef}>
                                <AppSubmenu
                                    items={item.items}
                                    onMenuItemClick={props.onMenuItemClick}
                                />
                            </div>
                        </CSSTransition>
                    </li>
                );
            }
        });

    return items ? (
        <ul className={props.className} role="menu">
            {items}
        </ul>
    ) : null;
};

interface AppMenuProps {
    layoutColorMode: string;
    model: MenuItem[];
    onMenuItemClick?: (event: any) => void;
}

export const AppMenu = (props: AppMenuProps) => {
    return (
        <div className="layout-menu-container">
            <AppSubmenu
                items={props.model}
                className="layout-menu"
                onMenuItemClick={props.onMenuItemClick}
                root={true}
                role="menu"
            />
        </div>
    );
};
